'use client';

import Modal from '@/components/Modal';
import ProductSizes from '@/components/ProductSizes';
import { RVI_LS_PROPERTY_NAME, base64Image } from '@/constants';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { useAddToCartMutation, useCart } from '@/hooks/cart';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { useWishlistProducts } from '@/hooks/product';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { useAddItemToWishlist, useDeleteItemFromWishlist } from '@/hooks/wishlistHooks';
import { IconArrowUp } from '@/icons/IconArrowUp';
import { IconCare } from '@/icons/IconCare';
import { IconFire } from '@/icons/IconFire';
import { IconHeart } from '@/icons/IconHeart';
import { IconInfoGraphic } from '@/icons/IconInfoGraphic';
import { IconMinus } from '@/icons/IconMinus';
import { IconPlus } from '@/icons/IconPlus';
import { IconScale } from '@/icons/IconScale';
import { IconShoppingBag } from '@/icons/IconShoppingBag';
import { ICartItems } from '@/interfaces/cart';
import { IProductDetails, IProductSize } from '@/interfaces/product';
import useHeaderStore from '@/store/header';
import { numberFormat } from '@/utils/helper';
import { Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import FloatingCart from '@/components/FloatingCart';
import { PageHeading } from '@/components/PageHeading';
import { getDataLayer } from '@/utils/data-layer';
import { EMISection } from '@/components/EMISectionComponent';
import FreeShippingInfo from './FreeShippingInfo';
import { IconExpress } from '@/icons/IconExpress';
import OnlinePaymentProductInfo from './OnlinePaymentProductInfo';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import { ProductPaymentMode } from '@/enums';
import NeuroMarketingProductDetails from './NeuroMarketingProductDetails';
import BundleOfferBadge from '@/components/BundlePromotion/BundleOfferBadge';
import BundleOfferPrompt from '@/components/BundlePromotion/BundleOfferPrompt';
import { useBundleOfferExplore } from '@/hooks/useBundleOfferExplore';

const ProductOptions = (props: {
  product: IProductDetails;
  quickView: boolean;
  productInfoGraphic?: number;
  setProductCardSeName?: (value: string) => void;
}) => {
  const { product, quickView, productInfoGraphic, setProductCardSeName } = props;
  const router = useRouter();
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  const [productSize, selectProductSize] = useState<number>(product?.IsGiftVoucher ? product?.Variations[0]?.ProductAttributeValue : -1);
  const [productQuantity, setProductQuantity] = useState<number>(1);
  const [sizeModal, setSizeModal] = useState<any>(false);
  const [careModal, setCareModal] = useState<any>(false);
  const [isBuyNow, setIsBuyNow] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<IProductSize | null>(null);
  const [shouldShowFloatingCart, setShouldShowFloatingCart] = useState<boolean>(false);
  const [pendingCartAction, setPendingCartAction] = useState<'buyNow' | 'addToCart' | null>(null);
  const { explore: exploreBundleOffers, isExploring } = useBundleOfferExplore();
  const { mutate: mutateAddToCart } = useAddToCartMutation(addToCartCallback);
  const { mutate: mutateAddToWishlist } = useAddItemToWishlist(true, product?.SeName, toggleProductWishlistState);
  const { mutate: deleteFromWishlist } = useDeleteItemFromWishlist(true, product?.SeName, toggleProductWishlistState);
  const { data: cartData } = useCart(queryClient, authObject?.session);
  const { data: wishListProduct } = useWishlistProducts(product?.SeName, authObject?.session, authObject?.isAuthenticated);
  const [showEMIModal, setShowEMIModal] = useState<boolean>(false);
  const openCartPanel = useHeaderStore((state) => state.openCartPanel);
  const cartPanel = useHeaderStore((state) => state.cartPanel);
  const myLocation = useHeaderStore((state) => state.myLocation);
  const { expressMessage } = useDeliveryOption();
  const showExpressOption = useHeaderStore((state) => state.showExpressOption);

  useEffect(() => {
    let stringifiedRVIs = localStorage.getItem(RVI_LS_PROPERTY_NAME);
    if (stringifiedRVIs) {
      try {
        let arrayOfProductIds = JSON.parse(stringifiedRVIs);
        if (Array.isArray(arrayOfProductIds)) {
          //arrayOfProductIds is a valid array
          let typedArrayOfProductIds = arrayOfProductIds as string[];
          const indexOfFoundSeName = typedArrayOfProductIds.findIndex((x) => x === product.Id);
          if (indexOfFoundSeName > -1) {
            typedArrayOfProductIds.splice(indexOfFoundSeName, 1);
          }
          typedArrayOfProductIds.unshift(product.Id);
          if (typedArrayOfProductIds.length > 15) {
            // trimming and keeping hte latest 15 visited product Ids only
            typedArrayOfProductIds.length = 15;
          }
          localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify(typedArrayOfProductIds));
        } else {
          localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
        }
      } catch (e) {
        localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
      }
    } else {
      localStorage.setItem(RVI_LS_PROPERTY_NAME, JSON.stringify([product.Id]));
    }
  }, [product.Id]);

  function addToCartCallback(shouldOpenCart: boolean = true, shouldOpenNotification: boolean = true) {
    if (shouldOpenCart) {
      openCartPanel(true);
    }
    if (shouldOpenNotification) {
      openNotification('success', 'Cart', 'Product added to cart successfully');
    }
    if (isBuyNow) {
      router.push('/checkout');
      setIsBuyNow(false);
    }

    setProductQuantity(1);
  }

  function toggleProductWishlistState(IsWishListed: boolean, productId: string) {
    product.IsWishListed = IsWishListed;
    queryClient.setQueryData([product.SeName], () => {
      return { ...product };
    });
  }

  const handleWishlistClick = () => {
    if (!wishListProduct?.IsWishListed) {
      mutateAddToWishlist({ productId: product.Id });
    } else {
      deleteFromWishlist(product.Id);
    }
  };

  const addToCart = (product: IProductDetails, shouldOpenCart: boolean = true, shouldOpenNotification: boolean = true) => {
    let tempSelectedSize = product.Variations?.find((x) => x.ProductAttributeValue == productSize);
    product.Variations?.forEach((x) => {
      if (x.ProductAttributeValue == productSize) {
        x.IsSelected = true;
      } else {
        x.IsSelected = false;
      }
    });

    let tempCartItem: ICartItems = {
      AvailableColors: product.AvailableColors,
      Id: product.Id + '_' + tempSelectedSize?.ProductAttributeValue,
      Pictures: product.ProductImageList,
      ProductId: product.Id,
      ProductName: product.Name,
      ProductSeName: product.SeName,
      Quantity: productQuantity,
      ProductSku: product.Sku,
      SubTotalValue: product.ProductPrice.Price,
      UnitOldPrice: product.ProductPrice.OldPrice,
      UnitPriceValue: product.ProductPrice.Price,
      Variations: product.Variations,
      Warnings: [],
      IsWishListed: product.IsWishListed,
      OrderMaximumQuantity: product.OrderMaximumQuantity,
      OrderMinimumQuantity: product.OrderMinimumQuantity,
      IsGiftVoucher: product.IsGiftVoucher,
      shouldOpenCart,
      shouldOpenNotification,
      Brand: product.ProductBrand?.Name,
      Category: product.CategoryName,
      Gender: product.Gender,
    };

    mutateAddToCart(tempCartItem);
  };

  const runAddToCart = () => {
    addToCart(product, false, true);
  };

  const runBuyNow = () => {
    const skuCheck = cartData?.Items?.find((item) => item.ProductSku === product?.Sku)?.Id;
    let sizeCheck = null;
    let breakParentLoop = false;
    if (cartData?.Items) {
      for (let i = 0; i < cartData?.Items?.length; i++) {
        if (cartData?.Items[i]?.ProductSku === product.Sku) {
          for (let j = 0; j < cartData?.Items[i]?.Variations.length; j++) {
            if (cartData?.Items[i]?.Variations[j].IsSelected) {
              if (cartData?.Items[i]?.Variations[j].ProductAttributeValue == productSize) {
                sizeCheck = productSize;
                breakParentLoop = true;
                break;
              }
            }
          }
        }
        if (breakParentLoop) {
          break;
        }
      }
    }
    if (!skuCheck || !sizeCheck) {
      addToCart(product, false);
    } else {
      router.push('/checkout');
    }
    setIsBuyNow(true);
    let obj: { [key: string]: string } = {};

    if (product?.CategoryName) {
      let splittedCategories = product?.CategoryName.split(',');
      splittedCategories.forEach((category, categoryIndex) => {
        obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
      });
    }
    if (!product?.IsGiftVoucher && product?.Gender) {
      obj['gender'] = product?.Gender;
    }
    const dataLayer = getDataLayer();
    if (dataLayer !== undefined && dataLayer) {
      dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'BDT',
          value: product?.ProductPrice?.Price,
          items: [
            {
              item_id: product?.SeName,
              item_name: product?.Name,
              item_sku: product?.Sku,
              price: product?.ProductPrice?.Price,
              quantity: productQuantity,
              item_variant: product.Variations?.find((x) => x.IsSelected)?.VariantSku,
              color: product?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
              size: product.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
              item_brand: product?.ProductBrand?.Name,
              ...obj,
            },
          ],
        },
      });
    }
  };

  // Shared by the "×"/backdrop close and "Maybe Later": runs the pending Buy Now or Add to Cart action the customer originally clicked.
  const runPendingAction = () => {
    const action = pendingCartAction;
    setPendingCartAction(null);
    if (action === 'buyNow') runBuyNow();
    else if (action === 'addToCart') runAddToCart();
  };

  // Ignored while Explore is in flight, otherwise the action would run and Explore would then navigate away too.
  const handleBundlePromptSkip = () => {
    if (isExploring) return;
    runPendingAction();
  };

  const handleBundlePromptExplore = async () => {
    const selectedSize = product.Variations?.find((x) => x.ProductAttributeValue == productSize)?.ProductAttributeValue ?? productSize;
    const explored = await exploreBundleOffers(product, { size: selectedSize, quantity: productQuantity });
    if (explored) setPendingCartAction(null);
    // No slab / group or a failed fetch: don't strand the customer, run what they originally clicked.
    else runPendingAction();
  };

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '20px',
    threshold: 0.0,
  });

  const matchingVariation = product.Variations?.find((variation) => variation.ProductAttributeValue == productSize);
  const showFreeShipping = product.IsFreeShipping || matchingVariation?.FreeShippingEnabled;

  const onProductSizeChange = (size: number) => {
    setProductQuantity(1);
    selectProductSize(size);
  };
  useEffect(() => {
    if (productSize !== -1) {
      setSelectedVariant(product?.Variations?.find((variant) => variant.ProductAttributeValue == productSize) || null);
    }
  }, [productSize]);

  useEffect(() => {
    selectProductSize(product?.IsGiftVoucher ? product?.Variations[0].ProductAttributeValue : -1);
  }, [product]);

  useEffect(() => {
    const actionContainerTop = containerRef.current?.getBoundingClientRect().top;
    if (!isVisible && actionContainerTop < 0) {
      setShouldShowFloatingCart(true);
    } else {
      setShouldShowFloatingCart(false);
    }
  }, [isVisible]);

  return (
    <>
      <PageHeading className="mb-2 leading-tight">{product.Name}</PageHeading>
      <div className="mb-2 text-lg">
        SKU: {product.Sku}
        <>{product.ProductStyles?.length ? ', Style: ' + product.ProductStyles.join(', ') : ''}</>
      </div>
      <div className="price text-2xl">
        {Number(product?.ProductPrice?.OldPrice) > Number(product?.ProductPrice?.Price) ? (
          <>
            <span className="text-gray-400 line-through">
              <span className="currency">৳</span>
              {numberFormat(product?.ProductPrice?.OldPrice)}
            </span>
            <span className="ml-4 whitespace-nowrap font-bold text-gray-600">
              <span className="currency">৳</span>
              {numberFormat(product?.ProductPrice?.Price)}
            </span>
          </>
        ) : (
          <span className="mr-4 font-bold text-gray-600">
            <span className="currency">৳</span>
            {numberFormat(product?.ProductPrice?.Price)}
          </span>
        )}
        {product.IsEligibleForEmi && (
          <div className="mt-2 flex items-center gap-2 text-lg font-semibold">
            EMI starts from BDT {numberFormat(Math.round(product?.EmiStartsFrom!))}
            <div
              data-testid="emi-view-details-button"
              className="hover:opacity-1 flex cursor-pointer items-center justify-center border-black px-2 underline transition duration-150 ease-linear hover:bg-gray-200 hover:text-black hover:opacity-80"
              onClick={() => {
                setShowEMIModal(true);
              }}
            >
              View Details
            </div>
          </div>
        )}
      </div>
      <div className="my-4">
        <div className="flex flex-wrap gap-2 text-xl">
          {product?.AvailableColors?.map((color, colorIndex) => (
            <div
              data-testid={`color-option-${color.ColorName?.toLowerCase().replaceAll(/\s+/g, '-')}`}
              className={`group relative aspect-square w-[70px] overflow-hidden border !border-solid ${
                color?.OwnColor ? 'border-gray-400' : 'border-gray-200 opacity-80'
              }`}
              key={color.ColorName}
            >
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-70 p-2 font-semibold text-white opacity-0 drop-shadow-[0_0_300px_#000] transition-opacity group-hover:opacity-100">
                <span className="text-center text-lg font-medium leading-tight">{color.ColorName}</span>
              </div>
              <Image
                className={`inline cursor-pointer object-cover`}
                src={color?.ProductDefaultImageUrl || '/images/product_default_img.png'}
                alt={color?.ProductSeName || ''}
                fill
                quality={100}
                onClick={() => {
                  if (!quickView) router.push(`${color?.ProductSeName}`);
                  if (quickView) {
                    setProductCardSeName && setProductCardSeName(color.ProductSeName);
                  }
                }}
                placeholder="blur"
                blurDataURL={base64Image}
                unoptimized={true}
              ></Image>
            </div>
          ))}
        </div>
      </div>

      {!product?.IsGiftVoucher && (
        <div data-testid="product-sizes-section">
          <ProductSizes
            product={product}
            productId={product?.Id}
            captchaId={product?.CaptchaPublicKey}
            sizes={product.Variations}
            selectedVariant={selectedVariant}
            onSelect={onProductSizeChange}
          />
        </div>
      )}

      <div className="product-quantity my-4 flex items-center">
        <div className="mb-2 mr-3 text-[1.5rem] font-bold">Quantity</div>
        <div className="duration-30 relative flex h-12 w-36 cursor-pointer items-center justify-center overflow-hidden rounded-[5px] border border-gray-400 text-xl transition-all">
          <button
            data-testid="decrease-quantity-button"
            title="Decrease quantity"
            onClick={() => {
              if (productQuantity > 1 && productSize !== -1) {
                setProductQuantity(productQuantity - 1);
              }
            }}
            className="!active:scale-100 cursor-pointer"
          >
            <IconMinus className={`${productQuantity > 1 && productSize !== -1 ? 'text-black' : 'text-gray-400'} w-3`} />
          </button>
          <span className="text-xl"> {productQuantity}</span>
          <button
            data-testid="increase-quantity-button"
            title="Increase quantity"
            onClick={() => {
              if (
                productSize !== -1 &&
                productQuantity < (product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity || 0)
              ) {
                setProductQuantity(productQuantity + 1);
              } else if (productSize === -1) {
                openNotification('danger', 'Error', 'Please select size.');
              } else if (productQuantity == product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity) {
                openNotification('danger', 'Error', 'Product quantity exceeded stock on hand');
              }
            }}
            className="!active:scale-100 cursor-pointer"
          >
            <IconPlus
              className={`${
                productSize !== -1 &&
                productQuantity < (product.Variations?.find((x) => +x.ProductAttributeValue === +productSize)?.StockQuantity || 0)
                  ? 'text-black'
                  : 'text-gray-400'
              } w-3`}
            />
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        {product.ProductAdditional?.SizeGuide === '' ? (
          <Tooltip label="Not Available" key="sizeGuide">
            <span className="flex select-none items-center gap-2 text-xl opacity-60">
              <IconScale className="w-7" />
              <span>Size guide</span>
            </span>
          </Tooltip>
        ) : (
          <button
            data-testid="size-guide-button"
            onClick={() => setSizeModal(true)}
            className="a flex cursor-pointer select-none items-center gap-2 border-0 bg-transparent p-0 text-xl"
            type="button"
          >
            <IconScale className="w-7" aria-hidden="true" />
            <span className="underline-custom">Size guide</span>
          </button>
        )}
        {product.ProductAdditional?.CareInstruction === '' ? (
          <Tooltip label="Not Available" key="careGuide">
            <span className="flex items-center gap-2 text-xl opacity-60">
              <IconCare className="w-7" />
              <span>Care guide</span>
            </span>
          </Tooltip>
        ) : (
          <button
            data-testid="care-guide-button"
            onClick={() => setCareModal(true)}
            className="flex cursor-pointer select-none items-center gap-2 border-0 bg-transparent p-0 text-xl"
            type="button"
          >
            <IconCare className="w-7" aria-hidden="true" />
            <span className="underline-custom">Care guide</span>
          </button>
        )}
        {product?.PaymentMode === ProductPaymentMode.Prepaid && <OnlinePaymentProductInfo />}
      </div>
      {selectedVariant?.IsAvailableInProvidedStores && Boolean(myLocation?.value) && showExpressOption && (
        <div className="mt-3 border border-gray-400 p-3">
          <div className="mb-1 flex pl-6 text-start text-lg">
            <div className="-ml-6 mr-2 min-w-[18px] text-center">
              <IconExpress className="w-7" />
            </div>
            <div>
              <strong className="mb-2">Express Delivery: </strong> {expressMessage || 'Same-Day Delivery Available — Order before 12 PM for Today!'}
            </div>
          </div>
        </div>
      )}
      <div className="mt-3 border border-gray-400 p-3">
        <div className="mb-1 flex pl-6 text-start text-lg">
          <div className="-ml-6 mr-2 min-w-[18px] text-center">
            <IconInfoGraphic className="h-[22.4px] w-5 text-green-600" />
          </div>
          <div>
            <strong className="mb-2">Viewed: </strong> {numberFormat(productInfoGraphic)} people recently VIEWED this product.
          </div>
        </div>
        <div className="mb-1 flex pl-6 text-start text-lg">
          <div className="-ml-6 mr-2 min-w-[18px] text-center">
            <IconFire className="-mt-1 w-4" />
          </div>
          <div>
            <strong className="mb-2">Popular: </strong> {numberFormat(product?.ProductPopularity)} people have BOUGHT this product.
          </div>
        </div>
        <FreeShippingInfo show={showFreeShipping} />
      </div>

      <NeuroMarketingProductDetails
        neuroMarketingMessages={product.NeuroMarketingMessages}
        productSize={productSize}
        variantStockInfo={product.Variations}
      />

      <div className="mt-5 space-y-2" id="add-to-cart-button-container">
        <div className="action-button-container" ref={containerRef}>
          {productSize !== -1 ? (
            <div className="flex gap-4">
              <button
                data-testid="buy-now-button"
                id="buy-now"
                className={'flex w-1/2 flex-grow cursor-pointer items-center justify-center bg-black !text-xl text-white'}
                onClick={() => {
                  if (productSize === -1) {
                    openNotification('danger', 'Purchase', 'Please select a size first.');
                    return;
                  }
                  if (product?.IsBundlePromotionAvailable) {
                    setPendingCartAction('buyNow');
                    return;
                  }
                  runBuyNow();
                }}
              >
                <span>Buy Now</span>
                <IconArrowUp className="ml-2 w-3.5 rotate-90" />
              </button>
              <button
                data-testid="add-to-cart-button"
                className="circle flex-flex-grow-1 flex w-1/2 cursor-pointer items-center justify-center border-2 border-black !text-xl transition duration-200 ease-linear hover:bg-black hover:text-white"
                onClick={() => {
                  if (productSize === -1) {
                    openNotification('danger', 'Cart', 'Please select a size first.');
                    return;
                  }
                  if (product?.IsBundlePromotionAvailable) {
                    setPendingCartAction('addToCart');
                    return;
                  }
                  runAddToCart();
                }}
              >
                Add To Cart
                <IconShoppingBag className={'ml-2 w-6' + (productSize === -1 ? ' text-gray-400' : '')} />
              </button>
            </div>
          ) : (
            <div>
              <button
                data-testid="select-size-button"
                className={'flex h-[42px] w-full flex-grow cursor-pointer items-center justify-center bg-black !text-xl text-white'}
                onClick={() => {
                  if (productSize == -1) {
                    openNotification('danger', 'Product Size', 'Please select a size first.');
                  }
                }}
              >
                <span>Select Size</span>
              </button>
            </div>
          )}
        </div>

        <button
          data-testid="wishlist-button"
          className="flex w-full cursor-pointer items-center justify-center border-2 border-black !text-xl transition duration-150 ease-linear hover:bg-black hover:text-white"
          onClick={() => {
            handleWishlistClick();
          }}
        >
          <span>{wishListProduct?.IsWishListed ? 'Remove from Wishlist' : 'Add to Wishlist'}</span>
          <IconHeart className="ml-2 w-6" filled={wishListProduct?.IsWishListed} />
        </button>

        {product?.IsBundlePromotionAvailable && <BundleOfferBadge className="w-full" />}
      </div>

      {shouldShowFloatingCart && !quickView && !cartPanel && (
        <FloatingCart
          product={product}
          addToCart={addToCart}
          productSize={productSize}
          selectProductSize={onProductSizeChange}
          setProductQuantity={setProductQuantity}
          productQuantity={productQuantity}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
        />
      )}
      <Modal heading="Size guide" open={sizeModal} closeModal={() => setSizeModal(false)} changeHeight={false} className="w-full !p-1 md:w-[50rem]">
        <div className="relative aspect-square">
          <Image
            src={product.ProductAdditional?.SizeGuide}
            alt="Size guide"
            fill
            quality={100}
            sizes="100%"
            className="object-contain"
            placeholder="blur"
            blurDataURL={base64Image}
            unoptimized={true}
          ></Image>
        </div>
      </Modal>
      {product.ProductAdditional?.CareInstruction && (
        <Modal heading="Care guide" open={careModal} closeModal={() => setCareModal(false)} className="w-full !p-1 md:w-[50rem]">
          <div className="relative aspect-square">
            <Image
              src={product.ProductAdditional?.CareInstruction}
              alt="Care guide"
              fill
              quality={100}
              sizes="100%"
              className="object-contain"
              placeholder="blur"
              blurDataURL={base64Image}
              unoptimized={true}
            ></Image>
          </div>
        </Modal>
      )}

      {/* EMI modal section  */}
      <Modal
        heading="EMI Options"
        open={showEMIModal}
        closeModal={() => {
          setShowEMIModal(false);
        }}
        className="w-full !p-1 md:w-[80rem]"
      >
        <Suspense>
          <EMISection showConfirmBtn={false} productPrice={product?.ProductPrice?.Price}></EMISection>
        </Suspense>
      </Modal>

      <BundleOfferPrompt
        open={pendingCartAction !== null}
        onClose={handleBundlePromptSkip}
        onSkip={handleBundlePromptSkip}
        onExplore={handleBundlePromptExplore}
        skipLabel={pendingCartAction === 'buyNow' ? 'Buy Now' : 'Add to Cart'}
        isExploring={isExploring}
      />
    </>
  );
};

export default ProductOptions;
